/**
 * MIME metadata helpers shared by every upload surface.
 *
 * Picker metadata is advisory: some native and browser providers omit it or
 * return application/octet-stream. These helpers recover a useful content
 * type from the user-visible name or source URI without requiring the user to
 * rename the file.
 */

const MIME_TYPE_BY_EXTENSION: Readonly<Record<string, string>> = {
  csv: "text/csv",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  gif: "image/gif",
  m4v: "video/x-m4v",
  mkv: "video/x-matroska",
  mov: "video/quicktime",
  mp4: "video/mp4",
  heic: "image/heic",
  jpeg: "image/jpeg",
  jpg: "image/jpeg",
  pdf: "application/pdf",
  png: "image/png",
  tif: "image/tiff",
  tiff: "image/tiff",
  txt: "text/plain",
  webp: "image/webp",
  webm: "video/webm",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

const GENERIC_MIME_TYPES = new Set([
  "",
  "application/octet-stream",
  "binary/octet-stream",
]);

function extensionFromPath(value: string | null | undefined): string | null {
  if (!value) return null;
  const path = value.split(/[?#]/, 1)[0] ?? value;
  const basename = path.split(/[\\/]/).at(-1) ?? path;
  const extension = basename.split(".").at(-1)?.trim().toLowerCase();
  return extension && extension !== basename ? extension : null;
}

export function inferMimeTypeFromFileName(
  name: string | null | undefined,
): string | null {
  const extension = extensionFromPath(name);
  return extension ? (MIME_TYPE_BY_EXTENSION[extension] ?? null) : null;
}

/**
 * Picker implementations do not all populate MIME metadata consistently.
 * Prefer the declared type, but recover a useful type from the name or URI
 * before falling back to the generic type accepted by the upload endpoint.
 */
export function resolveUploadContentType(file: {
  name?: string | null;
  uri?: string | null;
  mimeType?: string | null;
}): string {
  const declared = ((file.mimeType ?? "").split(";", 1)[0] ?? "")
    .trim()
    .toLowerCase();
  if (!GENERIC_MIME_TYPES.has(declared)) return declared;
  return (
    inferMimeTypeFromFileName(file.name) ??
    inferMimeTypeFromFileName(file.uri) ??
    "application/octet-stream"
  );
}
